import React, { useEffect, useRef, useState } from 'react';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import { formatCurrency } from '../../../../utils/currency';
import { CELL_SAVE_DEBOUNCE_MS } from '../../config';
import { notifyReadOnly } from '../../../../utils/supabaseErrors';
import './monthly-cell.scss';

interface MonthlyCellProps {
  projectId: number;
  stageId: number | null;
  year: number;
  month: number;
  forecast: number;
  actual: number;
  onForecastSaved: (year: number, month: number, newAmount: number) => void;
  // Pre-FY tracking lump-sum mode. Hides the "actual invoiced" sub-row and
  // skips variance colouring — actuals don't apply to pre-tracking work.
  isPrior?: boolean;
}

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

const MonthlyCell: React.FC<MonthlyCellProps> = ({
  projectId,
  stageId,
  year,
  month,
  forecast,
  actual,
  onForecastSaved,
  isPrior = false,
}) => {
  const supabase = useSupabaseClient();
  // Display the allowance with 2 decimals so it visually aligns with the
  // invoiced amount below (also 2 decimals) — otherwise an integer forecast
  // looks equal to an invoice with cents and the variance colouring confuses.
  const [draft, setDraft] = useState<string>(forecast > 0 ? forecast.toFixed(2) : '');
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedValue = useRef<number>(forecast);

  // Sync draft from the upstream forecast only on EXTERNAL changes (e.g.,
  // refetch). After our own save the parent re-renders with the value we
  // just persisted — skipping it here prevents in-flight keystrokes typed
  // during the save round-trip from being wiped.
  useEffect(() => {
    if (forecast === lastSavedValue.current) return;
    setDraft(forecast > 0 ? forecast.toFixed(2) : '');
    lastSavedValue.current = forecast;
  }, [forecast]);

  const persist = async (rawValue: string) => {
    const parsed = rawValue.trim() === '' ? 0 : parseFloat(rawValue);
    if (isNaN(parsed)) {
      setSaveState('error');
      return;
    }
    if (parsed === lastSavedValue.current) {
      return;
    }

    setSaveState('saving');
    const { error } = await supabase.rpc('upsert_project_forecast', {
      p_project_id: projectId,
      p_stage_id: stageId,
      p_year: year,
      p_month: month,
      p_amount: parsed,
    });

    if (error) {
      console.error('Forecast save error:', error);
      notifyReadOnly(error);
      setSaveState('error');
      return;
    }

    lastSavedValue.current = parsed;
    setSaveState('saved');
    onForecastSaved(year, month, parsed);
    setTimeout(() => setSaveState((s) => (s === 'saved' ? 'idle' : s)), 1200);
  };

  const handleChange = (value: string) => {
    setDraft(value);
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      persist(value);
    }, CELL_SAVE_DEBOUNCE_MS);
  };

  const handleBlur = () => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = null;
    }
    // Normalise to 2-decimal display on blur so the column visually matches
    // the invoice line below. parseFloat handles partials like ".5" or "1.".
    if (draft.trim()) {
      const parsed = parseFloat(draft);
      if (!isNaN(parsed)) {
        const normalised = parsed.toFixed(2);
        if (normalised !== draft) setDraft(normalised);
      }
    }
    persist(draft);
  };

  // Compare the SAVED forecast (not the draft) to the actual invoiced
  // amount so the colour reflects committed state, not in-flight typing.
  // Pre-FY cells have no meaningful "actual" — always neutral.
  const variance = (() => {
    if (isPrior) return 'neutral';
    if (actual <= 0) return 'neutral';            // future or no-invoice month
    const f = lastSavedValue.current;
    if (f <= 0) return 'orange';                  // invoiced with nothing allocated
    if (f > actual) return 'red';                 // over-forecast
    if (f < actual) return 'green';               // under-forecast
    return 'neutral';                              // exact match
  })();

  const varianceTitle =
    variance === 'red'
      ? 'Allocated more than invoiced this month'
      : variance === 'green'
        ? 'Invoiced more than allocated this month'
        : variance === 'orange'
          ? 'Invoiced this month with no allocation — add a fee plan and allocate'
          : undefined;

  return (
    <div className="monthly-cell">
      <div className="monthly-cell__forecast-row">
        <input
          type="number"
          inputMode="decimal"
          step="0.01"
          className={`monthly-cell__input is-${variance}`}
          value={draft}
          onChange={(e) => handleChange(e.target.value)}
          onBlur={handleBlur}
          placeholder="—"
          title={varianceTitle}
        />
        {saveState === 'saving' && <span className="monthly-cell__indicator monthly-cell__indicator--saving">…</span>}
        {saveState === 'saved' && <span className="monthly-cell__indicator monthly-cell__indicator--saved">✓</span>}
        {saveState === 'error' && <span className="monthly-cell__indicator monthly-cell__indicator--error">!</span>}
      </div>
      {!isPrior && (
        <div className={`monthly-cell__actual ${actual > 0 ? 'is-active' : ''}`}>
          {actual > 0 ? formatCurrency(actual) : '—'}
        </div>
      )}
    </div>
  );
};

export default MonthlyCell;
